import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BoardDTO } from '@contracts/workplace';
import { PaletteCardsContext, type PaletteCard } from '@/components/layout/command-palette';
import { fetchBoard } from './api';

/**
 * The Workplace board cache (`BOARD_KEY` in useBoard.ts), spelled out so the entry chunk does not pull the board's
 * model in with it; a test pins the two together. Only read here.
 */
export const PALETTE_BOARD_KEY = ['workplace', 'board'] as const;
/** The titles the palette offers, read from that cache or, with none, from the board endpoint (no API change). */
export const PALETTE_CARDS_KEY = ['command-palette', 'cards'] as const;

const NO_CARDS: readonly PaletteCard[] = [];

async function readCards(board: BoardDTO | undefined): Promise<PaletteCard[]> {
  let source = board;
  if (source === undefined) {
    const answer = await fetchBoard();
    source = 'unchanged' in answer ? undefined : answer;
  }
  return (source?.items ?? []).map((card) => ({ id: card.id, title: card.title, note: card.itemType === 'note' }));
}

/**
 * The ⌘K palette's "Thẻ" group (UX wave D, P7), for the signed-in shell: the frame reads no query cache, so this
 * sits around it (app.tsx). Nothing is read until the palette asks — once a word is typed — and each later ask reads
 * again, from the loaded board when there is one, so the titles are those of the moment.
 */
export function PaletteCardsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [wanted, setWanted] = useState(false);
  const read = useQuery({
    queryKey: PALETTE_CARDS_KEY,
    queryFn: () => readCards(queryClient.getQueryData<BoardDTO>(PALETTE_BOARD_KEY)),
    enabled: wanted,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // The first ask turns the read on; a later one (the palette opened again) reads afresh. Stable, so an ask is one read.
  const asked = useRef(false);
  const want = useCallback(() => {
    if (!asked.current) { asked.current = true; setWanted(true); return; }
    void queryClient.invalidateQueries({ queryKey: PALETTE_CARDS_KEY, exact: true });
  }, [queryClient]);
  const value = useMemo(() => ({ cards: read.data ?? NO_CARDS, want }), [read.data, want]);
  return <PaletteCardsContext.Provider value={value}>{children}</PaletteCardsContext.Provider>;
}
